/**
 * Chord Progression Demo
 *
 * Demonstrates how the SAME chord progression (I-vi-IV-V) sounds different
 * in different songs based on tempo and feel.
 */

import React, { useState } from "react";
import { useSoundLibrary } from "../../soundLibrary";
import { Note } from "../../types/sound";
import styles from "../../styles/ProgressionDemo.module.css";

// Chord definitions (all in C Major)
const CHORDS: Record<string, Note[]> = {
    C: ["C4", "E4", "G4"],
    Am: ["A3", "C4", "E4"],
    F: ["F3", "A3", "C4"],
    G: ["G3", "B3", "D4"],
};

// The I-vi-IV-V progression in C Major
const PROGRESSION = ["C", "Am", "F", "G"] as const;

// Bass note patterns for each chord (lower octave, higher octave)
const BASS_NOTES: Record<string, { low: Note; high: Note }> = {
    C: { low: "C3", high: "C4" },
    Am: { low: "A2", high: "A3" },
    F: { low: "F2", high: "F3" },
    G: { low: "G2", high: "G3" },
};

// Song versions using the SAME progression
const SONG_VERSIONS = {
    letItBe: {
        name: "Let It Be",
        artist: "The Beatles",
        style: "Ballad",
        emoji: "🕊️",
        bpm: 72,
        beatDuration: 833, // ms per beat (60000/72)
        velocity: 0.6,
        description: "Slow, gentle, sustained chords",
        useBassPattern: false,
    },
    heartAndSoul: {
        name: "Heart and Soul",
        artist: "Hoagy Carmichael",
        style: "Classic Doo-Wop",
        emoji: "🎹",
        bpm: 170,
        beatDuration: 353, // ms per beat (60000/170)
        velocity: 0.7,
        description: "Alternating bass octaves, half-note pattern",
        useBassPattern: true,
    },
} as const;

type SongVersion = keyof typeof SONG_VERSIONS;

export default function ProgressionDemo() {
    const { playChord, loadInstrument } = useSoundLibrary();
    const [selectedSong, setSelectedSong] = useState<SongVersion>("heartAndSoul");
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentChordIndex, setCurrentChordIndex] = useState<number | null>(null);
    const [currentBeat, setCurrentBeat] = useState(0);

    const handlePlay = async () => {
        if (isPlaying) return;

        setIsPlaying(true);
        await loadInstrument("piano");

        const song = SONG_VERSIONS[selectedSong];

        // Play each chord in the progression
        for (let i = 0; i < PROGRESSION.length; i++) {
            setCurrentChordIndex(i);
            const chordName = PROGRESSION[i];
            const notes = CHORDS[chordName];

            if (song.useBassPattern) {
                // Heart and Soul: alternating bass octaves (half notes)
                const bassNotes = BASS_NOTES[chordName];
                const halfNoteDuration = song.beatDuration * 2 / 1000; // 2 beats

                // Play low octave (beats 1-2)
                await playChord("piano", [bassNotes.low], {
                    duration: halfNoteDuration,
                    velocity: song.velocity,
                });
                setCurrentBeat(0);
                await new Promise(resolve => setTimeout(resolve, song.beatDuration * 2));

                // Play high octave (beats 3-4)
                await playChord("piano", [bassNotes.high], {
                    duration: halfNoteDuration,
                    velocity: song.velocity,
                });
                setCurrentBeat(2);
                await new Promise(resolve => setTimeout(resolve, song.beatDuration * 2));
            } else {
                // Let It Be: sustained chords
                await playChord("piano", notes, {
                    duration: song.beatDuration * 4 / 1000,
                    velocity: song.velocity,
                });

                // Beat indicator (4 beats)
                for (let beat = 0; beat < 4; beat++) {
                    setCurrentBeat(beat);
                    await new Promise(resolve => setTimeout(resolve, song.beatDuration));
                }
            }
        }

        setCurrentChordIndex(null);
        setCurrentBeat(0);
        setIsPlaying(false);
    };

    const song = SONG_VERSIONS[selectedSong];

    return (
        <div className={styles.demoContainer}>
            <div className={styles.header}>
                <h4 className={styles.title}>Progression Detective: Same Chords, Different Songs</h4>
                <p className={styles.subtitle}>
                    Hear how the I-vi-IV-V progression sounds completely different with tempo
                </p>
            </div>

            {/* The Progression Display */}
            <div className={styles.progressionDisplay}>
                <p className={styles.progressionLabel}>
                    The chords are <strong>identical:</strong>
                </p>
                <div className={styles.chordsRow}>
                    {PROGRESSION.map((chord, index) => (
                        <div
                            key={chord}
                            className={`${styles.chordBox} ${
                                currentChordIndex === index ? styles.chordBoxActive : ""
                            }`}
                        >
                            <div className={styles.chordName}>{chord}</div>
                            <div className={styles.romanNumeral}>
                                {chord === "C" ? "I" : chord === "Am" ? "vi" : chord === "F" ? "IV" : "V"}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Song Version Selector */}
            <div className={styles.songSelector}>
                {(Object.keys(SONG_VERSIONS) as SongVersion[]).map((version) => {
                    const songVersion = SONG_VERSIONS[version];
                    const isActive = selectedSong === version;
                    return (
                        <button
                            key={version}
                            className={`${styles.songButton} ${isActive ? styles.songButtonActive : ""}`}
                            onClick={() => setSelectedSong(version)}
                            disabled={isPlaying}
                        >
                            <span className={styles.songEmoji}>{songVersion.emoji}</span>
                            <div className={styles.songInfo}>
                                <div className={styles.songName}>{songVersion.name}</div>
                                <div className={styles.songArtist}>{songVersion.artist}</div>
                            </div>
                            <div className={styles.songStyle}>{songVersion.style}</div>
                        </button>
                    );
                })}
            </div>

            {/* Play Button */}
            <button
                className={styles.playButton}
                onClick={handlePlay}
                disabled={isPlaying}
            >
                {isPlaying ? "♪ Playing..." : `▶ Play "${song.name}"`}
            </button>

            {/* Active Song Info */}
            <div className={styles.songDetails}>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Tempo:</span>
                    <span className={styles.detailValue}>♩ = {song.bpm} BPM</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Style:</span>
                    <span className={styles.detailValue}>{song.description}</span>
                </div>

                {/* Beat Indicator */}
                {isPlaying && (
                    <div className={styles.beatIndicator}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div
                                key={i}
                                className={`${styles.beatDot} ${currentBeat === i ? styles.beatDotActive : ""}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Insight */}
            <div className={styles.insight}>
                <p className={styles.insightText}>
                    <strong>🎵 Key insight:</strong> Both songs use the <strong>exact same chord progression</strong> (I-vi-IV-V).
                    The only difference is tempo and feel. This is why learning progressions is so powerful—
                    mastering ONE pattern unlocks thousands of songs!
                </p>
            </div>
        </div>
    );
}
